import React, { useEffect, useMemo, useState } from "react";
import { Disclosure } from "@headlessui/react";
import { useRecoilState, useRecoilValue } from "recoil";
import PDFViewer from "../../PDFViewer";
import {
  getDataById,
  invoiceDescription,
  isAddInvoice,
  isAddState,
  isEditState,
  pdfData,
  pdfFileState,
  pendingData,
  voidedData,
} from "../atoms/atoms";
import {
  ChevronDoubleDownIcon,
  ClipboardDocumentListIcon,
} from "@heroicons/react/24/solid";
import CloseIconButton from "../../../../../components/CloseIconButton";
import SubmitHookForm from "../../../../../components/SubmitHookForm";
import { IItemize, IVendorInvoiceDetails } from "../types";
import TextInputLabelHookFormVIP from "../../../../../components/TextInputLabelHookFormVIP";
import { Controller, FormProvider, useForm } from "react-hook-form";
import SelectHookFormSingleVIP from "../../../../../components/SelectHookFormSingleVIP";
import ConfirmBoxModal from "../../../../../components/ConfirmBoxModal";
import { useEditInvoice } from "../hooks/useEditInvoice";
import CancelButton from "../../../../../components/CancelButton";
import { useGetOfficeVendors } from "../hooks/useGetOfficeVendors";
import EditButtonVendorVoice from "../../../../../components/EditButtonVendorVoice";
import { toast } from "../../../../../components/toastNotification";
import { useQueryClient } from "react-query";
import { getPdf } from "../../../../../actions/CarrierAgreementMgmt/VendorInvoice";
import { InvoiceView } from "../view/InvoiceView";
import {
  FIELDPERMISSIONS,
  PERMISSIONS,
} from "../../../../PermissionsConstants";
import { permissionsSelector } from "../../../../Login/selectors/permissionSelector";
import { VoidedInvoiceView } from "../view/VoidedInvoiceView";
import Modal from "../../../../../components/Modal";
import { useChangePdf } from "../hooks/useChangePdf";
import SaveSubmitOutlineButton from "../../../../../components/SaveSubmitOutlineButton";
import SubmitOutlineButton from "../../../../../components/SubmitOutlineButton";
import ViewOutlineButton from "../../../../../components/ViewOutlineButton";
import ChangeOutlineButton from "../../../../../components/ChangeOutlineButton";
import { InvoiceActivityHistoryView } from "../view/InvoiceActivityHistoryView";
import NumericInputLabel from "../../../../../components/NumericInputLabel";
import useGetExpenseAccount from "../hooks/useGetExpenseAccount";
import InvoiceItemizedView from "../view/InvoiceItemizedView";
import ItemizedFormField from "./ItemizedFormField";
import useGetInvoiceAutoPay from "../hooks/useGetInvoiceAutoPay";

interface Props {
  cancelShow;
  rowForEdit;
  activityHistoryData;
}

export const InvoiceForm: React.FC<Props> = ({
  cancelShow,
  rowForEdit,
  activityHistoryData,
}) => {
  const [viewPdf, setViewPdf] = useRecoilState(pdfData);
  const [addState, setAddState] = useRecoilState(isAddState);
  const [newData, setNewData] = useState<IVendorInvoiceDetails>();
  const [, setDataForReview] = useState<IVendorInvoiceDetails>();
  const [openConfirm, setOpenConfirm] = useState(false);
  const [buttonAction, setButtonAction] = useState<any>();
  const [, setGetInvoiceById] = useRecoilState<any>(getDataById);
  const [editState, setEditState] = useRecoilState(isEditState);
  const [pendingInvoiceDetails, setPendingInvoiceDetails] =
    useRecoilState<any>(pendingData);
  const [voidedInvoiceDetails, setVoidedInvoiceDetails] =
    useRecoilState<any>(voidedData);
  const [showUploadModal, setShowUploadModal] = useRecoilState(isAddInvoice);
  const [pdfFile, setPdfFile] = useRecoilState<any>(pdfFileState);
  const [attachment, setAttachment] = useState<File | null>();
  const [changePdfConfirm, setChangePdfConfirm] = useState(false);
  const [isPreviousBalance, setIsPreviousBalance] = useState(
    rowForEdit?.previousBalance ? true : false
  );
  const [serviceDueDate, setServiceDueDate] = useState(
    rowForEdit?.servicePeriodDate ?? null
  );
  const [billDueDate, setBillDueDate] = useState(
    rowForEdit?.billingPeriodEndDate ?? null
  );
  const [descriptionData, setDescriptionData] =
    useRecoilState<any>(invoiceDescription);
  const [isDescriptionChanged, setIsDescriptionChanged] = useState(false);
  const approver = FIELDPERMISSIONS.fieldpermission_vendorinvoice_approver;
  const analyst = FIELDPERMISSIONS.fieldpermission_vendorinvoice_analyst;
  const editPermission = PERMISSIONS.permission_vendorinvoice_edit;

  const userPermissions = useRecoilValue(permissionsSelector);
  const queryClient = useQueryClient();
  const useFormMethods = useForm<IVendorInvoiceDetails>({
    defaultValues: { ...rowForEdit },
  });

  const {
    register,
    formState: { errors, isDirty },
    control,
    watch,
    setValue,
    handleSubmit,
  } = useFormMethods;
  //Single Edit Initialization
  const {
    mutate: editVendorInvoice,
    isSuccess: editVendorInvoiceSuccess,
    data: editVendorInvoiceResult,
    reset: resetEditVendorInvoiceData,
  } = useEditInvoice();

  const calculateFieldSum = (data: IItemize[]) => {
    const sum = data?.reduce((sum, item) => {
      const itemAmount = parseFloat(item.amount?.toString());
      return isNaN(itemAmount) ? sum : sum + itemAmount;
    }, 0);
    return parseFloat(sum.toFixed(2));
  };
  const checkIfAmountPaidIsChanged = (amountPaid: number | null) => {
    return amountPaid !== rowForEdit.amountPaid ? true : false;
  };
  const onSubmit = (data: IVendorInvoiceDetails) => {
    const totalFieldSum = calculateFieldSum(data?.itemizeDetails);
    const amountPaidIsDirty = checkIfAmountPaidIsChanged(data?.amountPaid);

    if (totalFieldSum === data?.amountPaid) {
      if (buttonAction === "submit") {
        if (
          data.accountNumber === null ||
          data.amountPaid === null ||
          data.invoiceNumber === null ||
          data.servicePeriodDate == null ||
          data.billingPeriodEndDate === null
        ) {
          toast.fire(
            "Warning",
            "Please fill in all the required fields.",
            "warning"
          );
        } else {
          setNewData({
            ...data,
            id: rowForEdit.id,
            vendorInvoiceId: rowForEdit.id,
            invoiceDetailsId: rowForEdit.invoiceDetailsId,
            invoiceImageAttachment: rowForEdit.invoiceImageAttachment,
            createdBy: rowForEdit.createdBy,
            createdDate: rowForEdit.createdDate,
          });
          setOpenConfirm(true);
          setDescriptionData([]);
        }
      } else if (
        buttonAction === "saveSubmit" &&
        (isDirty || amountPaidIsDirty)
      ) {
        setNewData({
          ...data,
          id: rowForEdit.id,
          vendorInvoiceId: rowForEdit.id,
          invoiceDetailsId: rowForEdit.invoiceDetailsId,
          invoiceImageAttachment: rowForEdit.invoiceImageAttachment,
          createdBy: rowForEdit.createdBy,
          createdDate: rowForEdit.createdDate,
          newlyAdd: addState ? true : false,
        });
        setOpenConfirm(true);
        setDescriptionData([]);
      } else if (isDirty || amountPaidIsDirty) {
        setNewData({
          ...data,
          id: rowForEdit.id,
          vendorInvoiceId: rowForEdit.id,
          invoiceDetailsId: rowForEdit.invoiceDetailsId,
          invoiceImageAttachment: rowForEdit.invoiceImageAttachment,
          createdBy: rowForEdit.createdBy,
          createdDate: rowForEdit.createdDate,
          status: "Unverified",
          newlyAdd: addState ? true : false,
          isSave: true,
        });
        setOpenConfirm(true);
        setDescriptionData([]);
      } else {
        toast.fire("Saving Failed", "No changes have been made", "warning");
      }
    } else {
      toast.fire(
        "Warning",
        "The Total does not match the invoice amount.",
        "warning"
      );
    }
  };

  //Handle saving edit
  const handleSave = () => {
    editVendorInvoice(newData);
    setGetInvoiceById(undefined);
    setOpenConfirm(false);
  };

  //Handle submit for review
  const handleSubmitForReview = () => {
    editVendorInvoice({ ...rowForEdit, status: "For Review" });
    setOpenConfirm(false);
  };

  //Handle submit for review
  const handleSaveAndSubmit = () => {
    editVendorInvoice({ ...newData, status: "For Review" });
    setOpenConfirm(false);
  };

  //Toasters
  if (editVendorInvoiceSuccess && editVendorInvoiceResult) {
    if (
      editVendorInvoiceResult.status === 201 ||
      editVendorInvoiceResult.status === 200
    ) {
      toast.fire("Success", "Vendor invoice saved successfully.", "success");

      setEditState(false);
      setAddState(false);
      setDataForReview(editVendorInvoiceResult?.data);
      cancelShow();
      queryClient.invalidateQueries("GetPendingInvoices");
    }
    resetEditVendorInvoiceData();
  }

  const itemizedDetailsArray = watch("itemizeDetails");
  const setItemizationFieldValue = (e: any) => {
    if (
      Array.isArray(itemizedDetailsArray) &&
      itemizedDetailsArray?.length === 1
    ) {
      // Create a copy of the item at index 0 to avoid mutating the original state directly
      const updatedItem = { ...itemizedDetailsArray[0] };
      updatedItem.parentRow = true;
      updatedItem.tableName = "itemizeDetails";
      updatedItem.officeId = e ? e.officeId : 0;
      updatedItem.officeName = e ? e.officeName : null;
      updatedItem.glBranch = e ? e.branchGL : null;
      updatedItem.subAccount = e ? e.glsubaccounts : null;
      updatedItem.officeVendorServiceId = e ? e.recordNumber : 0;
      updatedItem.invoiceDetailsId = watch("invoiceDetailsId") ?? 0;
      updatedItem.isActive = true;
      const updatedArray = [...itemizedDetailsArray];
      updatedArray[0] = updatedItem;
      setValue("itemizeDetails", updatedArray);
    }
  };
  const officeAddress = () => {
    const street = watch("officeStreet");
    const address = street
      ? `${watch("officeStreet")}, ${watch("officeStateCode")} ${watch(
          "officeZipcode"
        )}`
      : `${watch("officeStateCode")} ${watch("officeZipcode")}`;
    return address;
  };

  const setItemizedExpenseValue = (e: any) => {
    if (
      Array.isArray(itemizedDetailsArray) &&
      itemizedDetailsArray?.length === 1
    ) {
      // Create a copy of the item at index 0 to avoid mutating the original state directly
      const updatedItem = { ...itemizedDetailsArray[0] };
      updatedItem.expenseAccountId = e ? e.value : null;
      updatedItem.expenseDescription = e ? e.description : null;

      const updatedArray = [...itemizedDetailsArray];
      updatedArray[0] = updatedItem;

      setValue("itemizeDetails", updatedArray);
    }
  };
  const setItemizedAmountValue = (value: any) => {
    if (
      Array.isArray(itemizedDetailsArray) &&
      itemizedDetailsArray?.length === 1
    ) {
      // Create a copy of the item at index 0 to avoid mutating the original state directly
      const updatedItem = { ...itemizedDetailsArray[0] };
      updatedItem.amount = value;
      updatedItem.extCost = value;

      const updatedArray = [...itemizedDetailsArray];
      updatedArray[0] = updatedItem;
      setValue("itemizeDetails", updatedArray);
    }
  };
  const setItemizedDueDate = () => {
    if (
      Array.isArray(itemizedDetailsArray) &&
      itemizedDetailsArray?.length >= 1
    ) {
      const updatedItemizedDetailsArray = itemizedDetailsArray.map(
        (data, index) => {
          const adddress = officeAddress();
          const updatedItem = { ...data };
          if (
            serviceDueDate !== null &&
            billDueDate !== null &&
            data?.officeId
          ) {
            const date = `${serviceDueDate} - ${billDueDate}`;
            updatedItem.transactionDescription = date;
          }
          return updatedItem;
        }
      );
      setValue("itemizeDetails", updatedItemizedDetailsArray);
    }
  };
  function formatDate(date) {
    if (!date) {
      return "N/A";
    }
    var d = new Date(date),
      month = "" + (d.getMonth() + 1),
      day = "" + d.getUTCDate(),
      year = d.getFullYear();
    if (month.length < 2) month = "0" + month;
    if (day.length < 2) day = "0" + day;
    return [month, day, year].join("/");
  }
  const hasNonEmptyValue = (obj) => {
    return Object.values(obj).some((value) => value !== "");
  };

  const setFieldDescription = (data) => {
    const accountNumberDisplay = data?.AccountNumber
      ? data?.AccountNumber
      : "N/A";
    const autoPayDescription = data?.AutoPay
      ? data?.AutoPay?.toLowerCase() === "eft"
        ? data?.BankAccount
        : `AUTOPAY ${data?.BankAccount}`
      : "";

    const dueDate = formatDate(data?.DueDate);
    const serviceDueDate = formatDate(data?.ServiceDueDate);
    const billDueDate = formatDate(data?.BillDueDate);
    const serviceDateRange = `Service date: ${serviceDueDate} to ${billDueDate}`;
    const description = `Account#: ${accountNumberDisplay} ${autoPayDescription} DUE ${dueDate}; ${serviceDateRange}`;

    setValue("description", description);
  };

  useEffect(() => {
    if (
      isDescriptionChanged &&
      serviceDueDate !== null &&
      billDueDate !== null
    ) {
      setItemizedDueDate();
    }
  }, [billDueDate, serviceDueDate, isDescriptionChanged]);
  useEffect(() => {
    if (isDescriptionChanged) {
      setIsDescriptionChanged(false);
      if (hasNonEmptyValue(descriptionData)) {
        setFieldDescription(descriptionData);
      } else {
        setValue("description", "");
      }
    } else if (rowForEdit?.duedate != null && isDescriptionChanged === false) {
      setFieldDescription({
        ...descriptionData,
        DueDate:
          descriptionData?.length === 0 || descriptionData?.DueDate === ""
            ? rowForEdit?.duedate
            : descriptionData?.DueDate,
      });
    }
  }, [descriptionData, isDescriptionChanged]);

  const date =
    watch("servicePeriodDate") && watch("billingPeriodEndDate")
      ? `${watch("servicePeriodDate")} - ${watch("billingPeriodEndDate")} `
      : "";
  //#region Options
  const { data: officeVendorData } = useGetOfficeVendors();

  const [officeVendorFieldOpts, setOfficeVendorFieldOpts] = useState([]);
  const officeVendorsOpt = useMemo(() => {
    let options = officeVendorData?.data
      ?.map(
        (x: {
          id: number;
          recordNumber: number;
          vendorId: number;
          vendorInvoiceId: number;
          vendorName: string;
          vendorUser: string;
          vendorPassword: string;
          bankAccount: string;
          utilityType: string;
          webPage: string;
          accountNumber: string;
          officeId: number;
          officeName: string;
          officeStatus: string;
          officeState: string;
          officeStateCode: string;
          officeCity: string;
          officeZipcode: string;
          officeStreet: string;
          officeBrand: string;
          vendorCode: string;
          glsubaccounts: string;
          storeNumber: string;
          duedate: string;
          branchGL: string;
          transactionDescription: string;
          isActive: boolean;
        }) => {
          return {
            id: x.id,
            value: x.recordNumber,
            label: (x.accountNumber + " - " + x.vendorName).trim(),
            recordNumber: x.recordNumber,
            vendorId: x.vendorId,
            vendorInvoiceId: x.vendorInvoiceId,
            vendorName: x.vendorName,
            vendorUser: x.vendorUser,
            vendorPassword: x.vendorPassword,
            bankAccount: x.bankAccount
              ? x.bankAccount.replace(/.(?=.{4})/g, "x")
              : null,
            utilityType: x.utilityType,
            webPage: x.webPage,
            officeId: x.officeId,
            officeName: x.officeName,
            officeStatus: x.officeStatus,
            officeState: x.officeState,
            officeStateCode: x.officeStateCode,
            officeCity: x.officeCity,
            officeZipcode: x.officeZipcode,
            officeStreet: x.officeStreet + " " + x.officeZipcode,
            officeBrand: x.officeBrand,
            vendorCode: x.vendorCode,
            glsubaccounts: x.glsubaccounts,
            storeNumber: x.storeNumber,
            duedate: x.duedate,
            branchGL: x.branchGL,
            accountNumber: x.accountNumber,
            isActive: x.isActive,
            transactionDescription:
              date !== ""
                ? date + x.transactionDescription
                : x.transactionDescription,
          };
        }
      )
      .filter(
        (obj, index, self) =>
          index === self.findIndex((t) => t.label === obj.label)
      );
    return options;
  }, [officeVendorData]);

  useEffect(() => {
    let associatedOffices = officeVendorData?.data?.filter(
      (item) =>
        item.accountNumber === watch("accountNumber") && watch("vendorName")
    );
    associatedOffices = associatedOffices?.filter(
      (obj, index, self) =>
        index === self.findIndex((t) => t.officeName === obj.officeName)
    );

    let options = associatedOffices?.map((x) => {
      return {
        label: x.officeName,
        value: x.recordNumber,
        officeId: x.officeId,
        branch: x.branchGL,
        subAccount: x.glsubaccounts,
        transactionDescription:
          date !== ""
            ? date + x.transactionDescription
            : x.transactionDescription,
        vendorId: x.vendorId,
        accountNumber: x.accountNumber,
      };
    });

    setOfficeVendorFieldOpts(options);
  }, [editState]);

  const setFieldValue = (e) => {
    if (e) {
      let associatedOffices = officeVendorData?.data?.filter(
        (item) =>
          item.accountNumber === e.accountNumber &&
          item.vendorName === e.vendorName
      );
      associatedOffices = associatedOffices.filter(
        (obj, index, self) =>
          index === self.findIndex((t) => t.officeName === obj.officeName)
      );
      let hqOffice = officeVendorData?.data?.find(
        (item) => item.officeId == 86
      );
      let selectedVendorDetails = associatedOffices.length > 1 ? hqOffice : e;
      setValue("accountNumber", e ? e.accountNumber : null);
      setValue("recordNumber", e ? e.recordNumber : null);
      setValue(
        "officeId",
        selectedVendorDetails ? selectedVendorDetails.officeId : null
      );
      setValue(
        "officeName",
        selectedVendorDetails ? selectedVendorDetails.officeName : null
      );
      setValue(
        "officeCity",
        selectedVendorDetails ? selectedVendorDetails.officeCity : null
      );
      setValue(
        "officeState",
        selectedVendorDetails ? selectedVendorDetails.officeState : null
      );
      setValue(
        "officeStateCode",
        selectedVendorDetails ? selectedVendorDetails.officeStateCode : null
      );
      setValue(
        "officeStreet",
        selectedVendorDetails ? selectedVendorDetails.officeStreet : null
      );
      setValue(
        "officeZipcode",
        selectedVendorDetails ? selectedVendorDetails.officeZipcode : null
      );
      setValue(
        "officeStatus",
        selectedVendorDetails ? selectedVendorDetails.officeStatus : null
      );
      setValue(
        "officeBrand",
        selectedVendorDetails ? selectedVendorDetails.officeBrand : null
      );
      setValue("vendorId", e ? e.vendorId : null);
      setValue("vendorName", e ? e.vendorName : null);
      setValue("vendorUser", e ? e.vendorUser : null);
      setValue("vendorPassword", e ? e.vendorPassword : null);
      setValue("vendorCode", e ? e.vendorCode : null);
      setValue("bankAccount", e ? e.bankAccount : null);
      setValue("webPage", e ? e.webPage : null);
      setValue("utilityType", e ? e.utilityType : null);
      setValue("glsubaccounts", e ? e.glsubaccounts : null);
      setValue(
        "branchGL",
        selectedVendorDetails ? selectedVendorDetails.branchGL : null
      );
      setValue(
        "storeNumber",
        selectedVendorDetails ? selectedVendorDetails.storeNumber : null
      );

      let options = associatedOffices?.map((x) => {
        return {
          label: x.officeName,
          value: x.recordNumber,
          officeId: x.officeId,
          branch: x.branchGL,
          subAccount: x.glsubaccounts,
          transactionDescription:
            date !== ""
              ? date + x.transactionDescription
              : x.transactionDescription,
          vendorId: x.vendorId,
          accountNumber: x.accountNumber,
        };
      });

      setOfficeVendorFieldOpts(options);
    }
  };

  const previousBalanceOpt = [
    { label: "Yes", value: true },
    { label: "No", value: false },
  ];

  const { data: expenseAccountData, isFetching: EAIsLoading } =
    useGetExpenseAccount();
  const expenseAccountOpt =
    expenseAccountData &&
    expenseAccountData?.map((x) => {
      return {
        value: x.id,
        label: x.expenseAccount + " - " + x.description,
        description: x.description,
      };
    });
  const expenseAccountFieldOpt = expenseAccountData?.map((x) => {
    return {
      value: x.id,
      label: x.expenseAccount,
      description: x.description,
    };
  });

  const { data: autoPayData, isFetching: APIsLoading } = useGetInvoiceAutoPay();
  const autoPayOpts =
    autoPayData &&
    autoPayData
      ?.filter((y) => y.autoPayName !== null)
      ?.map((x) => {
        return {
          value: x.id,
          label: x.autoPayName,
        };
      });
  //#endregion
  //View pdf on new tab
  const retrieveFile = async (fileName) => {
    try {
      getPdf(fileName).then((content) => {
        const pdfBlob = new Blob([content.data], { type: "application/pdf" });
        const pdfUrl = URL.createObjectURL(pdfBlob);
        window.open(pdfUrl);
      });
    } catch (error) {}
  };

  const handleCancel = () => {
    setOpenConfirm(false);
    setPdfFile(null);
    setShowUploadModal(false);
  };

  const {
    mutate: changePdf,
    isSuccess: changePdfSuccess,
    data: changePdfData,
    reset: resetChangePdfData,
  } = useChangePdf();

  if (changePdfSuccess && changePdfData) {
    if (changePdfData?.status === 201 || changePdfData?.status === 200) {
      toast.fire(
        "Success",
        "The vendor invoice PDF has been updated successfully.",
        "success"
      );
      cancelShow();
      queryClient.invalidateQueries("GetPendingInvoices");
    } else {
      toast.fire("Server Error", "Cannot save PDF", "error");
    }
    resetChangePdfData();
  }

  //set file upon upload
  const fileType = ["application/pdf"];
  const handleChange = (e) => {
    let selectedFile = e.target.files[0];
    if (selectedFile) {
      if (
        selectedFile &&
        fileType.includes(selectedFile.type) &&
        e.target.files.length > 0
      ) {
        const newFile = e.target.files ? e.target.files.item(0) : null;
        setAttachment(newFile);
        let reader = new FileReader();
        reader.readAsDataURL(selectedFile);
        reader.onload = (e) => {
          setPdfFile(e?.target?.result);
        };
      } else {
        setPdfFile(null);
      }
    } else {
      setPdfFile(null);
    }
  };
  //#endregion

  const handleChangePdf = () => {
    setOpenConfirm(false);
    setShowUploadModal(false);
    handleSaveChangePdf();
  };

  const handleSaveChangePdf = () => {
    const VendorInvoiceData = {
      attachment: attachment,
      id: rowForEdit?.invoiceDetailsId,
    };
    changePdf(VendorInvoiceData);
  };

  const onCLose = () => {
    setGetInvoiceById(undefined);
    setPendingInvoiceDetails(undefined);
    setVoidedInvoiceDetails(undefined);
    cancelShow();
    setViewPdf(null);
    setAddState(false);
    setEditState(false);
  };

  const handlePreviousBalance = (e) => {
    if (e?.value === true) {
      setIsPreviousBalance(true);
      setValue("previousBalanceAmount", null);
    } else {
      setIsPreviousBalance(false);
      setValue("previousBalanceAmount", 0);
    }
  };

  return (
    <>
      <div className="box-shadow border-quinary border-l-2 p-2">
        <div className="flex gap-2 items-center  p-2 border-secondary border-b-2">
          <div>
            <ClipboardDocumentListIcon className="text-white bg-blue-500 rounded-md h-7 w-7 p-1" />
          </div>
          <div className="flex flex-col leading tight">
            <h3 className="text-xs font-medium text-gray-500">
              Capture Batch List
            </h3>
            <h1 className="mr-auto font-bold text-primary text-md uppercase  ">
              All Batches
            </h1>
          </div>
        </div>
        <div className="relative -top-16 -right-1 bg-gray-50">
          <CloseIconButton
            onClick={() => {
              onCLose();
            }}
          />
        </div>
        <div className="p-2 m-2 bg-gray-50 border rounded-md">
          <FormProvider {...useFormMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="flex justify-between bg-white rounded border  p-2 rounded-md">
                <div className="text-secondary">
                  <strong>Batch ID: </strong>
                  {rowForEdit?.id}
                </div>
                <div className="flex gap-1">
                  {userPermissions.includes(editPermission) && (
                    <div className="flex flex-row gap-2">
                      {editState || addState ? (
                        <>
                          <SubmitHookForm
                            type="submit"
                            disabled={false}
                            buttonText={"Save"}
                            className={undefined}
                            onClick={() => {
                              // checkDuedate();
                              setButtonAction("save");
                            }}
                          />
                          <CancelButton
                            type="button"
                            onClick={() => {
                              if (addState) {
                                onCLose();
                              } else {
                                setEditState(false);
                              }
                            }}
                          />
                        </>
                      ) : (
                        <EditButtonVendorVoice
                          disabled={false}
                          onClick={() => {
                            setEditState(true);
                          }}
                        />
                      )}
                    </div>
                  )}
                  {(userPermissions.includes(analyst) ||
                    userPermissions.includes(approver)) && (
                    <>
                      <ChangeOutlineButton
                        type="button"
                        disabled={editState}
                        buttonText={"Change PDF"}
                        className={undefined}
                        onClick={() => {
                          setShowUploadModal(true);
                        }}
                      />
                      <ViewOutlineButton
                        type="button"
                        disabled={false}
                        buttonText={"View PDF"}
                        className={undefined}
                        onClick={() => {
                          retrieveFile(rowForEdit?.invoiceImageAttachment);
                        }}
                      />
                      <SubmitOutlineButton
                        type="submit"
                        disabled={editState || addState}
                        buttonText={"Submit"}
                        className={undefined}
                        onClick={() => {
                          // checkDuedate();
                          setButtonAction("submit");
                        }}
                      />
                      <SaveSubmitOutlineButton
                        type="submit"
                        disabled={editState || addState ? false : true}
                        buttonText={"Save and Submit"}
                        className={undefined}
                        onClick={() => {
                          // checkDuedate();
                          setButtonAction("saveSubmit");
                        }}
                      />
                    </>
                  )}
                </div>
              </div>
              <div className="bg-white border mt-2 rounded-md">
                <div className="flex flex-row justify-between gap-4 p-4">
                  {/* Fields */}
                  <div className="w-full bg-white p-2 overflow-x-auto shadow-md rounded">
                    {/* Invoice Details - Voided  */}
                    {voidedInvoiceDetails &&
                      voidedInvoiceDetails?.map((data, index) => {
                        return (
                          <>
                            <Disclosure>
                              {({ open }) => (
                                <>
                                  <div className="m-2 bg-white rounded-lg box-shadow">
                                    <div className="flex flex-row justify-between">
                                      <div className="text-quinary font-bold font-sans py-2 px-3">
                                        Invoice Details {index + 1} - Voided
                                      </div>
                                      <div>
                                        <Disclosure.Button className="disclosureBtn ml-2 justify-end">
                                          <ChevronDoubleDownIcon
                                            className={`${
                                              open ? "transform rotate-180" : ""
                                            }  text-gray-500 w-5 h-5 mr-4`}
                                          />
                                        </Disclosure.Button>
                                      </div>
                                    </div>
                                    <Disclosure.Panel className="text-xxs">
                                      <VoidedInvoiceView rowData={data} />
                                    </Disclosure.Panel>
                                  </div>
                                </>
                              )}
                            </Disclosure>
                          </>
                        );
                      })}
                    {/* Invoice Details  */}
                    <Disclosure defaultOpen>
                      {({ open }) => (
                        <>
                          <div className="m-2 bg-white rounded-lg box-shadow">
                            <div className="flex flex-row justify-between">
                              <div className="text-quinary font-bold font-sans py-2 px-3">
                                Invoice Details{" "}
                                {pendingInvoiceDetails
                                  ? pendingInvoiceDetails?.length + 1
                                  : 1}{" "}
                                - Unverified
                              </div>
                              <div>
                                <Disclosure.Button className="disclosureBtn ml-2 justify-end">
                                  <ChevronDoubleDownIcon
                                    className={`${
                                      open ? "transform rotate-180" : ""
                                    }  text-gray-500 w-5 h-5 mr-4`}
                                  />
                                </Disclosure.Button>
                              </div>
                            </div>
                            <Disclosure.Panel className="text-xxs">
                              {editState || addState ? (
                                <div
                                  className="w-full borderContainer bg-white p-2"
                                  style={{
                                    width: "auto",
                                    height: "800px",
                                    overflow: "auto",
                                  }}
                                >
                                  <div className="grid grid-cols-1 md:grid-cols-2 mx-4  pb-2 2xl:gap-7 gap-2 ">
                                    {/* Left fields */}
                                    <div className="space-y-2 pl-2 pt-1">
                                      <Controller
                                        name="recordNumber"
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field }) => (
                                          <SelectHookFormSingleVIP
                                            field={field}
                                            onChange={(e) => {
                                              field.onChange(
                                                e ? e.value : null
                                              );
                                              setFieldValue(e);
                                              setItemizationFieldValue(e);
                                              setDescriptionData({
                                                ...descriptionData,
                                                AccountNumber: e
                                                  ? e.accountNumber
                                                  : "",
                                                BankAccount: e
                                                  ? e.bankAccount
                                                  : "",
                                              });
                                              setIsDescriptionChanged(true);
                                            }}
                                            value={field.value}
                                            options={
                                              officeVendorsOpt
                                                ? officeVendorsOpt
                                                : []
                                            }
                                            label="Service No. Account"
                                            className={
                                              errors.recordNumber &&
                                              "border-2 border-red-500"
                                            }
                                            required={true}
                                            isDisabled={false}
                                          />
                                        )}
                                      />
                                      <TextInputLabelHookFormVIP
                                        name="storeNumber"
                                        label="Corporate Office Number"
                                        type="text"
                                        register={register("storeNumber", {})}
                                        maxLength={50}
                                        disabled={true}
                                        required={true}
                                        className={
                                          errors.storeNumber &&
                                          "border-2 border-red-500"
                                        }
                                      />
                                      <TextInputLabelHookFormVIP
                                        name="officeStatus"
                                        label="Office Status"
                                        type="text"
                                        register={register("officeStatus", {})}
                                        maxLength={50}
                                        disabled={true}
                                      />
                                      <TextInputLabelHookFormVIP
                                        name="officeName"
                                        label="Confie Office Name"
                                        type="text"
                                        register={register("officeName", {})}
                                        maxLength={50}
                                        disabled={true}
                                      />
                                      <TextInputLabelHookFormVIP
                                        name="officeStreet"
                                        label="Office Street"
                                        type="text"
                                        register={register("officeStreet", {})}
                                        maxLength={50}
                                        disabled={true}
                                      />
                                      <TextInputLabelHookFormVIP
                                        name="officeZipcode"
                                        label="Zipcode"
                                        type="text"
                                        register={register("officeZipcode", {})}
                                        maxLength={50}
                                        disabled={true}
                                      />
                                      <TextInputLabelHookFormVIP
                                        name="officeCity"
                                        label="Office City"
                                        type="text"
                                        register={register("officeCity", {})}
                                        maxLength={50}
                                        disabled={true}
                                      />
                                      <TextInputLabelHookFormVIP
                                        name="officeState"
                                        label="Office State"
                                        type="text"
                                        register={register("officeState", {})}
                                        maxLength={50}
                                        disabled={true}
                                      />
                                      <TextInputLabelHookFormVIP
                                        name="officeBrand"
                                        label="Confie Brand"
                                        type="text"
                                        register={register("officeBrand", {})}
                                        maxLength={50}
                                        disabled={true}
                                      />
                                      {/* <TextInputLabelHookFormVIP
                                        name="taxId"
                                        label="Tax ID"
                                        type="text"
                                        register={register("taxId", {})}
                                        maxLength={50}
                                        disabled={!editState}
                                      /> */}
                                      <Controller
                                        control={control}
                                        name="amountPaid"
                                        rules={{ required: true }}
                                        render={() => (
                                          <NumericInputLabel
                                            name="amountPaid"
                                            label="Amount Paid"
                                            register={register(
                                              "amountPaid",
                                              {}
                                            )}
                                            required={true}
                                            pattern="/^\d*\.?\d*$/i,"
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            placeholder=""
                                            className={
                                              errors.amountPaid &&
                                              "border-2 border-red-500 col-span-2 "
                                            }
                                            secondDiv={"grid space-y-2"}
                                            onBlur={(e) => {
                                              setValue(
                                                "amountPaid",
                                                e ? Number(e?.target?.value) : 0
                                              );
                                              setItemizedAmountValue(
                                                e ? e.target.value?.trim() : 0
                                              );
                                            }}
                                          />
                                        )}
                                      />
                                      {/* <Controller
                                        control={control}
                                        name="amountPaid"
                                        rules={{ required: true }}
                                        render={() => (
                                          <TextInputLabelHookFormVIP
                                            name="amountPaid"
                                            label="Amount Paid"
                                            type="text"
                                            register={register(
                                              "amountPaid",
                                              {}
                                            )}
                                            maxLength={50}
                                            disabled={false}
                                            required={true}
                                            className={
                                              errors.amountPaid &&
                                              "border-2 border-red-500"
                                            }
                                          />
                                        )}
                                      /> */}
                                      <Controller
                                        control={control}
                                        name="servicePeriodDate"
                                        rules={{ required: true }}
                                        render={() => (
                                          <TextInputLabelHookFormVIP
                                            name="servicePeriodDate"
                                            label="Billing Period Start Date"
                                            type="date"
                                            register={register(
                                              "servicePeriodDate",
                                              {}
                                            )}
                                            maxLength={50}
                                            disabled={false}
                                            required={true}
                                            className={
                                              errors.servicePeriodDate &&
                                              "border-2 border-red-500"
                                            }
                                            onChange={(e) => {
                                              setServiceDueDate(
                                                e ? e.target.value : null
                                              );
                                              setDescriptionData({
                                                ...descriptionData,
                                                ServiceDueDate: e
                                                  ? e.target.value
                                                  : "",
                                              });
                                              setIsDescriptionChanged(true);
                                            }}
                                          />
                                        )}
                                      />
                                      <Controller
                                        control={control}
                                        name="billingPeriodEndDate"
                                        rules={{ required: true }}
                                        render={() => (
                                          <TextInputLabelHookFormVIP
                                            name="billingPeriodEndDate"
                                            label="Billing Period End Date"
                                            type="date"
                                            register={register(
                                              "billingPeriodEndDate",
                                              {}
                                            )}
                                            maxLength={50}
                                            disabled={false}
                                            required={true}
                                            className={
                                              errors.billingPeriodEndDate &&
                                              "border-2 border-red-500"
                                            }
                                            onChange={(e) => {
                                              setBillDueDate(
                                                e ? e.target.value : null
                                              );
                                              setDescriptionData({
                                                ...descriptionData,
                                                BillDueDate: e
                                                  ? e.target.value
                                                  : "",
                                              });
                                              setIsDescriptionChanged(true);
                                            }}
                                          />
                                        )}
                                      />
                                      <Controller
                                        control={control}
                                        name="invoiceNumber"
                                        rules={{ required: true }}
                                        render={() => (
                                          <TextInputLabelHookFormVIP
                                            name="invoiceNumber"
                                            label="Invoice No."
                                            type="text"
                                            register={register(
                                              "invoiceNumber",
                                              {}
                                            )}
                                            maxLength={50}
                                            disabled={false}
                                            required={true}
                                            className={
                                              errors.invoiceNumber &&
                                              "border-2 border-red-500"
                                            }
                                          />
                                        )}
                                      />
                                      <TextInputLabelHookFormVIP
                                        name="dateCapture"
                                        label="Date Capture"
                                        type="text"
                                        register={register("dateCapture", {})}
                                        maxLength={50}
                                        disabled={true}
                                      />
                                      <TextInputLabelHookFormVIP
                                        name="bankReference"
                                        label="Bank Reference"
                                        type="text"
                                        register={register("bankReference", {})}
                                        maxLength={50}
                                        disabled={false}
                                      />
                                      <TextInputLabelHookFormVIP
                                        name="bankReferenceDate"
                                        label="Bank Reference Date"
                                        type="date"
                                        register={register(
                                          "bankReferenceDate",
                                          {}
                                        )}
                                        disabled={false}
                                      />
                                      <Controller
                                        name="expenseAccountId"
                                        control={control}
                                        rules={{ required: false }}
                                        render={({ field }) => (
                                          <SelectHookFormSingleVIP
                                            field={field}
                                            onChange={(e) => {
                                              field.onChange(
                                                e ? e?.value : null
                                              );
                                              setValue(
                                                "expenseAccountDescription",
                                                e ? e?.label : null
                                              );
                                              setValue(
                                                "expenseDescription",
                                                e ? e?.description : null
                                              );
                                              setItemizedExpenseValue(e);
                                            }}
                                            value={field.value}
                                            options={
                                              expenseAccountOpt
                                                ? expenseAccountOpt
                                                : []
                                            }
                                            isLoading={EAIsLoading}
                                            label="Expense Account"
                                            className={
                                              errors.expenseAccountId &&
                                              "border-2 border-red-500"
                                            }
                                            required={false}
                                            isDisabled={false}
                                          />
                                        )}
                                      />
                                      <Controller
                                        name="autoPayId"
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field }) => (
                                          <SelectHookFormSingleVIP
                                            field={field}
                                            onChange={(e) => {
                                              field.onChange(
                                                e ? e?.value : null
                                              );
                                              setDescriptionData({
                                                ...descriptionData,
                                                AutoPay: e ? e.label : "",
                                              });
                                              setIsDescriptionChanged(true);
                                            }}
                                            value={field.value}
                                            options={
                                              autoPayOpts ? autoPayOpts : []
                                            }
                                            isLoading={APIsLoading}
                                            label="Auto Pay"
                                            className={
                                              errors.autoPayId &&
                                              "border-2 border-red-500"
                                            }
                                            required={true}
                                            isDisabled={false}
                                          />
                                        )}
                                      />
                                      <TextInputLabelHookFormVIP
                                        name="description"
                                        label="Description"
                                        type="text"
                                        register={register("description", {})}
                                        disabled={true}
                                      />
                                      {/* <TextInputLabelHookFormVIP
                                        name="billReference"
                                        label="Bill Reference"
                                        type="text"
                                        register={register("billReference", {})}
                                        maxLength={50}
                                        disabled={true}
                                      />
                                      <TextInputLabelHookFormVIP
                                        name="paymentReference"
                                        label="Payment Reference No."
                                        type="text"
                                        register={register(
                                          "paymentReference",
                                          {}
                                        )}
                                        maxLength={50}
                                        disabled={true}
                                      />
                                       */}
                                    </div>
                                    {/* Right fields */}
                                    <div className="space-y-2 pl-2 pt-1 mr-2">
                                      <TextInputLabelHookFormVIP
                                        name="vendorName"
                                        label="Vendor Name"
                                        type="text"
                                        register={register("vendorName", {})}
                                        maxLength={50}
                                        disabled={true}
                                      />
                                      <TextInputLabelHookFormVIP
                                        name="vendorCode"
                                        label="Vendor ID"
                                        type="text"
                                        register={register("vendorCode", {})}
                                        maxLength={50}
                                        disabled={true}
                                      />
                                      <TextInputLabelHookFormVIP
                                        name="bankAccount"
                                        label="Bank Account"
                                        type="text"
                                        register={register("bankAccount", {})}
                                        maxLength={50}
                                        disabled={true}
                                      />
                                      <TextInputLabelHookFormVIP
                                        name="utilityType"
                                        label="Utility Type"
                                        type="text"
                                        register={register("utilityType", {})}
                                        maxLength={50}
                                        disabled={true}
                                      />
                                      <TextInputLabelHookFormVIP
                                        name="branchGL"
                                        label="GL Branch"
                                        type="text"
                                        register={register("branchGL", {})}
                                        disabled={true}
                                      />
                                      <TextInputLabelHookFormVIP
                                        name="glsubaccounts"
                                        label="Subaccounts"
                                        type="text"
                                        register={register("glsubaccounts", {})}
                                        maxLength={50}
                                        disabled={true}
                                      />
                                      <Controller
                                        control={control}
                                        name="duedate"
                                        rules={{ required: true }}
                                        render={() => (
                                          <TextInputLabelHookFormVIP
                                            name="duedate"
                                            label="Due Date"
                                            type="date"
                                            register={register("duedate", {})}
                                            maxLength={50}
                                            disabled={false}
                                            required={true}
                                            className={
                                              errors.duedate &&
                                              "border-2 border-red-500"
                                            }
                                            onChange={(e) => {
                                              setDescriptionData({
                                                ...descriptionData,
                                                DueDate: e
                                                  ? e.target.value
                                                  : "",
                                              });
                                              setIsDescriptionChanged(true);
                                            }}
                                          />
                                        )}
                                      />
                                      <Controller
                                        name="previousBalance"
                                        control={control}
                                        rules={{ required: false }}
                                        render={({ field }) => (
                                          <SelectHookFormSingleVIP
                                            field={field}
                                            onChange={(e) => {
                                              field.onChange(
                                                e ? e.value : false
                                              );
                                              handlePreviousBalance(e);
                                            }}
                                            value={field.value}
                                            options={previousBalanceOpt}
                                            label="Previous Balance"
                                            className={""}
                                            required={false}
                                            isDisabled={false}
                                          />
                                        )}
                                      />
                                      <Controller
                                        control={control}
                                        name="previousBalanceAmount"
                                        rules={{ required: isPreviousBalance }}
                                        render={() => (
                                          <TextInputLabelHookFormVIP
                                            name="previousBalanceAmount"
                                            label="Previous Balance Amount"
                                            type="text"
                                            register={register(
                                              "previousBalanceAmount",
                                              {}
                                            )}
                                            maxLength={50}
                                            disabled={!isPreviousBalance}
                                            required={isPreviousBalance}
                                            className={
                                              errors.previousBalanceAmount &&
                                              "border-2 border-red-500"
                                            }
                                          />
                                        )}
                                      />
                                      <TextInputLabelHookFormVIP
                                        name="draftDate"
                                        label="Draft Date"
                                        type="date"
                                        register={register("draftDate", {})}
                                        disabled={false}
                                      />
                                      <TextInputLabelHookFormVIP
                                        name="invoiceImageAttachment"
                                        label="Invoice Image"
                                        type="text"
                                        register={register(
                                          "invoiceImageAttachment",
                                          {}
                                        )}
                                        maxLength={50}
                                        disabled={true}
                                      />
                                      <TextInputLabelHookFormVIP
                                        name="noteSection"
                                        label="Note"
                                        type="text"
                                        register={register("noteSection", {})}
                                        maxLength={50}
                                        disabled={false}
                                      />
                                      <TextInputLabelHookFormVIP
                                        name="createdBy"
                                        label="Created By"
                                        type="text"
                                        register={register("createdBy", {})}
                                        maxLength={50}
                                        disabled={true}
                                      />{" "}
                                      <TextInputLabelHookFormVIP
                                        name="createdDate"
                                        label="Created Date"
                                        type="text"
                                        register={register("createdDate", {})}
                                        maxLength={50}
                                        disabled={true}
                                      />
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <InvoiceView rowData={rowForEdit} />
                              )}
                            </Disclosure.Panel>
                          </div>
                        </>
                      )}
                    </Disclosure>
                  </div>
                  {/* PDF */}
                  <div className="w-full bg-white p-4 overflow-x-auto box-shadow rounded">
                    <h1>{rowForEdit?.invoiceImageAttachment}</h1>
                    <PDFViewer pdfUrl={viewPdf} />
                  </div>
                </div>
              </div>

              {/* Allocation Section */}
              <div className="w-full bg-white mt-2">
                <Disclosure defaultOpen={true}>
                  <>
                    <div className=" bg-white rounded-lg box-shadow">
                      {editState || addState ? (
                        ""
                      ) : (
                        <div className="flex flex-row justify-between mt-2">
                          <div className="text-quinary font-bold font-sans py-2 px-3">
                            Allocation Section
                          </div>
                        </div>
                      )}
                      <Disclosure.Panel className="text-xxs">
                        {editState || addState ? (
                          <>
                            <ItemizedFormField
                              itemizeDetails={
                                rowForEdit?.itemizeDetails &&
                                rowForEdit?.itemizeDetails?.length > 0
                                  ? rowForEdit?.itemizeDetails
                                  : undefined
                              }
                              officeVendorOpts={
                                officeVendorFieldOpts?.length > 0
                                  ? officeVendorFieldOpts
                                  : []
                              }
                              expenseAccountOpt={expenseAccountFieldOpt}
                            />
                          </>
                        ) : (
                          <InvoiceItemizedView
                            rowData={
                              watch("itemizeDetails") &&
                              watch("itemizeDetails")?.length > 0
                                ? watch("itemizeDetails")
                                : []
                            }
                            invoicePhase={"InvoiceCapture"}
                          />
                        )}
                      </Disclosure.Panel>
                    </div>
                  </>
                </Disclosure>
              </div>
            </form>
          </FormProvider>

          {/* Activity History */}
          <div className="w-full bg-white ">
            <Disclosure>
              {({ open }) => (
                <>
                  <div className=" bg-white rounded-lg box-shadow">
                    <div className="flex flex-row justify-between mt-2">
                      <div className="text-quinary font-bold font-sans py-2 px-3">
                        Activity History
                      </div>
                      <div>
                        <Disclosure.Button className="disclosureBtn ml-2 justify-end">
                          <ChevronDoubleDownIcon
                            className={`${
                              open ? "transform rotate-180" : ""
                            }  text-gray-500 w-5 h-5 mr-4`}
                          />
                        </Disclosure.Button>
                      </div>
                    </div>
                    <Disclosure.Panel className="text-xxs">
                      <InvoiceActivityHistoryView
                        activityHistoryData={activityHistoryData}
                      />
                    </Disclosure.Panel>
                  </div>
                </>
              )}
            </Disclosure>
          </div>
        </div>
      </div>
      {/* Confirmation modal */}
      {openConfirm && (
        <ConfirmBoxModal
          ModalTitle={
            addState
              ? "Add Vendor Invoice"
              : buttonAction === "submit"
              ? "Submit Vendor Invoice"
              : "Edit Vendor Invoice"
          }
          qConfirmation={
            addState
              ? "Are you sure you want to add this invoice?"
              : buttonAction === "save"
              ? "Are you sure you want to update this invoice?"
              : "Are you sure you want to submit this invoice?"
          }
          isOpen={openConfirm}
          onClose={() => setOpenConfirm(false)}
          onClickFunction={() => {
            if (buttonAction === "save") {
              handleSave();
            } else if (buttonAction === "submit") {
              handleSubmitForReview();
            } else {
              handleSaveAndSubmit();
            }
          }}
          cancelButtonRef={undefined}
        />
      )}

      {/* Upload file modal */}
      {showUploadModal && (
        <Modal
          isCloseHide={true}
          modalFilterStat=""
          className=""
          width={""}
          title={""}
          isOpen={showUploadModal}
          onClose={undefined}
          hasCloseButton={false}
        >
          <div className="container">
            <div className="flex justify-between">
              <div className="ml-2 font-bold text-primary text-md">
                {"Change PDF"}
              </div>
              <div className="flex justify-between gap-2 mr-2">
                <SubmitHookForm
                  type="button"
                  disabled={false}
                  buttonText={"Save"}
                  className={undefined}
                  onClick={() => {
                    if (pdfFile === null) {
                      toast.fire(
                        "Information",
                        "Please upload pdf file to proceed.",
                        "info"
                      );
                    } else {
                      setChangePdfConfirm(true);
                    }
                  }}
                />
                <CancelButton
                  onClick={() => {
                    handleCancel();
                  }}
                />
              </div>
            </div>

            <input
              type="file"
              className="form-control"
              onChange={handleChange}
            />
          </div>
        </Modal>
      )}

      {/*Change PDF Confirmation modal */}
      {changePdfConfirm && (
        <ConfirmBoxModal
          ModalTitle={"Change Vendor Invoice PDF"}
          qConfirmation={"Are you sure you want to upload this file?"}
          isOpen={changePdfConfirm}
          onClose={() => setChangePdfConfirm(false)}
          onClickFunction={() => {
            handleChangePdf();
          }}
          cancelButtonRef={undefined}
        />
      )}
    </>
  );
};
